import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Textarea } from "@material-tailwind/react";
import Label from "components/Label/Label";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import Multiselect from "multiselect-react-dropdown";
import { FC, HTMLAttributes, useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { getFields, resetValues } from "redux/features/signUp/signUpSlice";
import Avatar from "shared/Avatar/Avatar";
import Input from "shared/Input/Input";
import Select from "shared/Select/Select";
import { z } from "zod";
import __cities from "../../data/jsons/cities.json";
import OnboardingTour, { stepStyle } from "utils/tours/OnboardingTour";
import { useLocation } from "react-router-dom";
import PhoneInput, { phoneSchema } from "shared/Phone/PhoneInput";

const MAX_FILE_SIZE = 1024 * 1024 * 10;
const ACCEPTED_IMAGE_MIME_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];

const formSchema = z.object({
  designation: z.string().min(1, { message: "Le designation est obligatoire." }),
  category: z.string().min(1, { message: "Le category est obligatoire." }),
  phone: phoneSchema,
  //link: z.string(),
  link: z
    .string()
    .optional()
    .refine((value) => !value || /^(https?:\/\/)/.test(value), {
      message: "Le lien doit commencer par http:// ou https://",
    }),
  region: z.string({ required_error: "Le region est obligatoire." }),
  contact: z.string(),
  description: z.string(),
  instagram: z
    .string()
    .optional()
    .refine((value) => !value || /^https:\/\/www\.instagram\.com\//.test(value), {
      message: "Le lien Instagram doit commencer par https://www.instagram.com/",
    }),
  youtube: z
    .string()
    .optional()
    .refine((value) => !value || /^https:\/\/www\.youtube\.com\//.test(value), {
      message: "Le lien Youtube doit commencer par https://www.youtube.com/",
    }),

  facebook: z
    .string()
    .optional()
    .refine((value) => !value || /^https:\/\/www\.facebook\.com\//.test(value), {
      message: "Le lien Facebook doit commencer par https://www.facebook.com/",
    }),
  fields: z.string().min(1, { message: "Les domaines sont obligatoires." }),
  availability: z.array(z.string()).nonempty({ message: "Les régions disponibles est obligatoire." }),
  service_url: z
    .optional(z.instanceof(File))
    .refine(
      (file) => !file || (file?.size <= MAX_FILE_SIZE && ACCEPTED_IMAGE_MIME_TYPES.includes(file?.type)),
      "Le fichier doit être une image au format JPEG, PNG ou GIF et ne pas dépasser 10MB."
    ),
});

export interface ProfieServiceFormProps {
  className?: string;
  isLoading: boolean;
  handleForm: Event;
  errorServiceForm?: string;
  FormData: (data: any) => void;
}

const ProfileServiceForm: FC<ProfieServiceFormProps & HTMLAttributes<HTMLDivElement>> = ({
  className,
  isLoading,
  handleForm,
  errorServiceForm,
  FormData,
}) => {
  const [phoneInputColor, setPhoneInputColor] = useState("");
  useEffect(() => {
    let theme = localStorage.getItem("theme");
    if (theme === "dark") {
      setPhoneInputColor("#000");
    } else {
      setPhoneInputColor("#000");
    }
  }, []);

  const phoneInputStyle = {
    color: phoneInputColor,
    borderRadius: "0.375rem",
  };
  const dispatch = useAppDispatch();
  const signUpState = useAppSelector((state) => state.signUp);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    trigger,
    watch,
    control,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      link: "",
      contact: "",
      description: "",
      facebook: "",
      youtube: "",
      instagram: "",
      fields: "",
      availability: [],
    },
    mode: "onSubmit",
  });

  useEffect(() => {
    dispatch(getFields());
  }, []);

  useEffect(() => {
    if (handleForm.type === "chekProfileServiceEvent") {
      handleSubmit(onSubmit)();
    }
  }, [handleForm]);

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (data) => {
    const fullPhone = `33${data.phone}`;
    const updatedData = { ...data };

    FormData(updatedData);
  };

  const steps = [
    {
      selector: ".image",
      content: "Une image est une représentation visuelle.",
      stepStyle,
    },
    {
      selector: ".nom-prenom",
      content: "Ajouter votre prénom en complément de votre nom de famille.",
      stepStyle,
    },
    {
      selector: ".type",
      content: "Ajouter votre prénom en complément de votre nom de famille.",
      stepStyle,
    },
    {
      selector: ".num-telephone ",
      content:
        "Un numéro de téléphone est une séquence unique et nominative de chiffres permettant de vous contacter. ",
    },
    {
      selector: ".site ",
      content: "Votre site web. ",
    },
    {
      selector: ".ville",
      content: "Région de logements éparpillés dans la campagne..",
      stepStyle,
    },
    {
      selector: ".contact",
      content: "Votre contact.",
      stepStyle,
    },
    {
      selector: ".facebook",
      content: "Votre Facebook.",
      stepStyle,
    },
    {
      selector: ".instagram",
      content: "Votre Instagram.",
      stepStyle,
    },
    {
      selector: ".youtube",
      content: "Votre Youtube.",
      stepStyle,
    },
    {
      selector: ".propos",
      content: "Mentionnez les meilleures caractéristiques de votre services, équipements spéciaux.",
      stepStyle,
    },
    {
      selector: ".type-service",
      content: "Votre type de service.",
      stepStyle,
    },
    {
      selector: ".region",
      content: "Votre Region.",
      stepStyle,
    },
  ];

  const location = useLocation();
  useEffect(() => {
    const cleanup = () => {
      if (signUpState.currentStep === 2) {
        dispatch(resetValues());
      }
    };

    if (location.pathname !== "/signup/service") {
      dispatch(resetValues());
    }

    return cleanup;
  }, [location.pathname, signUpState.currentStep]);

  useEffect(() => {
    getValues("fields");
  }, [watch("fields")]);

  return (
    <div className={className}>
      <div className="flex justify-center mb-8">
        <div className="flex flex-col items-center image">
          <div className="relative group">
            <div className="relative flex overflow-hidden rounded-full border-4 border-white shadow-lg">
              <Avatar
                sizeClass="w-32 h-32"
                imgUrl={getValues("service_url") ? URL.createObjectURL(getValues("service_url") as File) : ""}
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer rounded-full">
                <svg className="w-8 h-8 text-white mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span className="text-xs text-white font-medium">
                  {getValues("service_url") ? "Changer" : "Ajouter"} une photo
                </span>
              </div>
              <input
                type="file"
                id="imageFile"
                name="imageFile"
                className="absolute inset-0 opacity-0 cursor-pointer"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setValue("service_url", file);
                    trigger("service_url");
                  }
                }}
                accept="image/jpeg, image/jpg, image/png, image/webp"
              />
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 text-center">
              Photo de profil (optionnel)
            </p>
          </div>
        </div>
      </div>

      {errorServiceForm && (
        <Alert color="red" className="text-xs">
          {errorServiceForm}
        </Alert>
      )}

      <form className="space-y-8">
        {/* Section informations de base */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
            <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            Informations personnelles
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="nom-prenom">
              <Label>Nom et prénom *</Label>
              <Input
                type="text"
                placeholder="Nom et prénom"
                className={`mt-1.5 ${errors.designation?.message ? "border-red-500" : ""}`}
                disabled={isLoading}
                {...register("designation")}
              />
              {errors.designation?.message && (
                <div className="text-xs text-red-500 mt-1">{errors.designation?.message}</div>
              )}
            </div>

            <div className="type">
              <Label>Type de compte *</Label>
              <Select
                className={`mt-1.5 ${errors.category?.message ? "border-red-500" : ""}`}
                disabled={isLoading}
                value={getValues("category")}
                onChange={(e) => setValue("category", e.target.value)}
              >
                <option value={""}>Choisir votre type de compte</option>
                <option value="company">Entreprise</option>
                <option value="particular">Particulier</option>
              </Select>
              {errors.category?.message && (
                <div className="text-xs text-red-500 mt-1">Ce champ est obligatoire</div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-8">
            <div className="num-telephone">
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <PhoneInput value={field.value || ""} onChange={field.onChange} error={errors.phone?.message} />
                )}
              />
            </div>

            <div className="ville">
              <Label>Ville *</Label>
              <Select
                style={{ minWidth: "306px" }}
                className={` ${errors.region?.message ? "border-red-500" : ""}`}
                disabled={isLoading}
                placeholder="Choix de ville"
                value={getValues("region")}
                onChange={(e) => setValue("region", e.target.value)}
              >
                <option value={""}>Choix de ville</option>

                {__cities?.map((region: any) => (
                  <option key={region.label} value={region.label}>
                    {`${region.label}`}
                  </option>
                ))}
              </Select>

              {errors.region?.message && <div className="text-xs text-red-500">{errors.region?.message}</div>}
            </div>

            {/*       */}
          </div>

          <div className="flex flex-wrap gap-6 x">
            <div className="w-1/2 site">
              <Label>Site web</Label>
              <Input type="text" placeholder="https://example.com" disabled={isLoading} {...register("link")} />
              {errors.link?.message && <div className="mt-2 text-xs text-red-500">{errors.link?.message}</div>}
            </div>
          </div>
          <div className="flex flex-wrap gap-6 x">
            <div className="w-1/2 contact">
              <Label>Contact</Label>
              <Input
                type="text"
                placeholder="example@mail.com"
                className="mt-1.5"
                disabled={isLoading}
                {...register("contact")}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-6 x">
            <div className="w-1/2 facebook">
              <Label>Facebook </Label>
              <Input
                className={`mt-1.5 ${errors.facebook?.message ? "border-red-500" : ""}`}
                type="text"
                placeholder="https://www.facebook.com/"
                disabled={isLoading}
                {...register("facebook")}
              />
              {errors.facebook?.message && (
                <div className="mt-2 text-xs text-red-500">{errors.facebook?.message}</div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-6 x">
            <div className="w-1/2 instagram">
              <Label>instagram</Label>
              <Input
                className={`mt-1.5 ${errors.instagram?.message ? "border-red-500" : ""}`}
                type="text"
                placeholder="https://instagram.com/"
                disabled={isLoading}
                {...register("instagram")}
              />
              {errors.instagram?.message && (
                <div className="mt-2 text-xs text-red-500">{errors.instagram?.message}</div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-6 x">
            <div className="w-1/2 youtube">
              <Label>Youtube</Label>
              <Input
                className={`mt-1.5 ${errors.youtube?.message ? "border-red-500" : ""}`}
                type="text"
                placeholder="https://www.youtube.com/"
                disabled={isLoading}
                {...register("youtube")}
              />
              {errors.youtube?.message && (
                <div className="mt-2 text-xs text-red-500">{errors.youtube?.message}</div>
              )}
            </div>
          </div>
          <div className="space-y-8">
            <div className="propos">
              <div className="">
                <h2 className="text-2xl font-semibold">A propos</h2>
                <span className="block mt-2 text-sm text-neutral-500 dark:text-neutral-400">
                  Mentionnez vos spécialités, vos atouts et ce qui vous distingue en tant que prestataire.
                </span>
              </div>
              <Textarea placeholder="..." rows={14} disabled={isLoading} {...register("description")} />
            </div>

            <div className="type">
              <Label>Type de prestation *</Label>
              <Select
                className={`mt-1.5 ${errors.fields?.message ? "border-red-500" : ""}`}
                disabled={isLoading}
                value={getValues("fields")}
                onChange={(e) => setValue("fields", e.target.value)}
              >
                <option value={""}>Séléctionner votre type de prestation</option>

                {signUpState.fields?.map((field) => (
                  <option key={field.designation} value={field.designation}>
                    {field.designation}
                  </option>
                ))}
              </Select>

              {errors.fields?.message && <div className="text-xs text-red-500">Ce champs est obligatoire</div>}
            </div>
          </div>

          <div className="region">
            <span className="block mb-3 text-lg font-semibold">
              Les régions disponibles pour vos prestations *
            </span>
            <Multiselect
              options={__cities}
              //selectedValues={}
              onSelect={(selectedList) => {
                const labels = selectedList.map((item: { label: any }) => item.label);
                setValue("availability", labels);
              }}
              onRemove={(selectedList) => {
                const labels = selectedList.map((item: { label: any }) => item.label);
                setValue("availability", labels);
              }}
              displayValue="label"
              className={errors.availability?.message ? "border-red-500" : ""}
              placeholder="Select regions"
              closeOnSelect={false}
              disable={isLoading}
            />

            {errors.availability?.message && (
              <div className="text-xs text-red-500">{errors.availability?.message.toString()}</div>
            )}
          </div>

          <div>
            {errors.service_url?.message && (
              <div className="text-xs text-red-500">{errors.service_url?.message.toString()}</div>
            )}
          </div>
        </div>

        {/* Conditions d'utilisation */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
          <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
            En cliquant sur <span className="font-semibold">Finaliser mon inscription</span>, j'accepte les{" "}
            <a
              href="/terms"
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:text-blue-700 underline transition-colors"
            >
              Conditions générales d'utilisation
            </a>{" "}
            et je reconnais avoir pris connaissance de la{" "}
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:text-blue-700 underline transition-colors"
            >
              Politique de confidentialité
            </a>{" "}
            d'EventToBee.
          </p>
        </div>
      </form>
      <OnboardingTour steps={steps} page_name={"ProfileServiceForm"}></OnboardingTour>
    </div>
  );
};

export default ProfileServiceForm;
